import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Search, Compass, Menu, X, LayoutDashboard, LogOut, UserCircle, ChevronDown } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", exact: true },
  { to: "/articles", label: "Articles" },
  { to: "/journals", label: "Journals by Subject" },
  { to: "/special-issues", label: "Special Issues" },
  { to: "/for-authors", label: "For Authors" },
  { to: "/for-reviewers", label: "For Reviewers" },
  {
    label: "About",
    children: [
      { to: "/about", label: "About Us" },
      { to: "/peer-review", label: "Peer Review Process" },
      { to: "/open-access", label: "Open Access Policy" },
      { to: "/publication-ethics", label: "Publication Ethics" },
    ],
  },
  { to: "/contact", label: "Contact Us" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isActive = (l) => (l.exact ? location.pathname === l.to : location.pathname.startsWith(l.to));
  const isGroupActive = (l) => l.children?.some((ch) => location.pathname === ch.to);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-200 dark:border-slate-800 shadow-sm">
      {/* Row 1 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-4 h-24">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0 group">
            <img src="/noventra-logo.png" alt="Noventra Academic Press" className="w-20 h-20 rounded-lg object-cover bg-white group-hover:scale-105 transition-transform" />
            <span className="leading-tight">
              <span className="block font-serif font-bold text-primary dark:text-slate-100 text-3xl">Noventra</span>
              <span className="block text-sm font-mono uppercase tracking-[0.18em] text-amber-600">Academic Press</span>
            </span>
          </Link>

          <form onSubmit={submitSearch} className="hidden md:flex items-center flex-1 max-w-md ml-auto">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="nav-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search papers, journals, authors…"
                className="pl-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 border-transparent focus-visible:bg-white dark:focus-visible:bg-slate-900"
              />
            </div>
          </form>

          <div className="hidden md:flex items-center gap-2.5 shrink-0 ml-2">
            <Button asChild data-testid="nav-submit-cta" className="rounded-full px-5 text-sm font-semibold">
              <Link to="/submit">Submit Paper</Link>
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" data-testid="nav-user-menu" className="gap-1.5 rounded-full px-4 text-sm font-semibold">
                    <UserCircle className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuLabel className="truncate">
                    {user.email}
                    <span className="block text-xs font-normal capitalize text-amber-600">{user.role}</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="nav-dashboard-link">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { logout(); navigate("/"); }} data-testid="nav-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="outline" data-testid="nav-login-btn" className="rounded-full px-5 text-sm font-semibold">
                <Link to="/login">Sign in</Link>
              </Button>
            )}
          </div>

          <button className="md:hidden ml-auto p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Row 2 — primary nav */}
      <nav className="hidden md:block border-t border-slate-100 dark:border-slate-800 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <ul className="flex items-center gap-1">
            {LINKS.map((l) => (
              l.children ? (
                <li key={l.label}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="nav-about-dropdown"
                        className={`inline-flex items-center gap-1 px-5 py-3 text-base font-semibold transition-colors border-b-2 ${
                          isGroupActive(l) ? "text-white border-amber-400" : "text-slate-300 border-transparent hover:text-white hover:border-white/30"
                        }`}
                      >
                        {l.label} <ChevronDown className="w-3.5 h-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      {l.children.map((ch) => (
                        <DropdownMenuItem key={ch.to} onClick={() => navigate(ch.to)} data-testid={`nav-about-${ch.to.replace(/\//g, "")}`}>
                          {ch.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </li>
              ) : (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`inline-block px-5 py-3 text-base font-semibold transition-colors border-b-2 ${
                      isActive(l)
                        ? "text-white border-amber-400"
                        : "text-slate-300 border-transparent hover:text-white hover:border-white/30"
                    }`}
                  >
                    {l.label}
                  </Link>
                </li>
              )
            ))}
          </ul>
        </div>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-4 space-y-1">
          <form onSubmit={submitSearch} className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="nav-search-input-mobile" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="pl-9" />
          </form>
          {LINKS.map((l) => (
            l.children ? (
              <div key={l.label} className="py-1">
                <p className="py-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{l.label}</p>
                <div className="pl-3 space-y-1 border-l border-slate-200 dark:border-slate-800">
                  {l.children.map((ch) => (
                    <Link key={ch.to} to={ch.to} data-testid={`nav-mobile-about-${ch.to.replace(/\//g, "")}`} className={`block py-1.5 text-sm ${location.pathname === ch.to ? "text-primary dark:text-sky-400" : "text-slate-600 dark:text-slate-400"}`}>
                      {ch.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link key={l.to} to={l.to} className={`block py-2 text-sm font-medium ${isActive(l) ? "text-primary dark:text-sky-400" : "text-slate-700 dark:text-slate-300"}`}>
                {l.label}
              </Link>
            )
          ))}
          <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex gap-2">
            <Button asChild size="sm" className="rounded-full flex-1"><Link to="/submit">Submit Paper</Link></Button>
            {user ? (
              <Button asChild variant="outline" size="sm" className="rounded-full flex-1"><Link to="/dashboard">Dashboard</Link></Button>
            ) : (
              <Button asChild variant="outline" size="sm" className="rounded-full flex-1"><Link to="/login">Sign in</Link></Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
